import { useState, useEffect } from 'react';
import s from './PromptBlock.module.css';
import { useSelector } from 'react-redux';
import {
    selectCurrentProject,
    selectIsRefining,
    selectRefinedText,
} from '../../../../store/selectors/projectSelectors';
import Button from '../../../../shared/ui/button/Button';

import IconShorten from '../../../../shared/ui/icons/IconShorten';
import IconVary from '../../../../shared/ui/icons/IconVary';
import IconSimplify from '../../../../shared/ui/icons/IconSimplify';
import IconElaborate from '../../../../shared/ui/icons/IconElaborate';
import IconWand from '../../../../shared/ui/icons/IconWand';
import IconExtend from '../../../../shared/ui/icons/IconExtend';
import { useAppDispatch } from '../../../../store/store';
import { setRefinedText } from '../../../../store/slices/projectSlice';
import { refineText } from '../../../../shared/api/textProjectApi';
import ReactQuill from 'react-quill';

type ImproveTextBlockProps = {
    quillRef: React.RefObject<ReactQuill>;
};

const ImproveTextBlock: React.FC<ImproveTextBlockProps> = ({ quillRef }) => {
    const currentProject = useSelector(selectCurrentProject);
    const improvedText = useSelector(selectRefinedText);
    const isGenerating = useSelector(selectIsRefining);
    const [selectedText, setSelectedText] = useState('');
    const [selectedAction, setSelectedAction] = useState<string | null>(null);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!quillRef?.current) return;

        const quill = quillRef.current.getEditor();

        const handleSelectionChange = (range: any) => {
            if (!range || range.length === 0) {
                setSelectedText('');
                return;
            }
            const text = quill.getText(range.index, range.length).trim();
            setSelectedText(text || '');
        };

        quill.on('selection-change', handleSelectionChange);

        return () => {
            quill.off('selection-change', handleSelectionChange);
        };
    }, [quillRef]);

    const improvementOptions = [
        {
            label: 'Rephrase',
            icon: <IconWand />,
            action: 'reword',
        },
        // {
        //     label: 'Shorten',
        //     icon: <IconShorten />,
        //     action: 'shorten',
        // },
        {
            label: 'Expand',
            icon: <IconExtend />,
            action: 'long',
        },
        // {
        //     label: 'Vary',
        //     icon: <IconVary />,
        //     action: 'vary',
        // },
        {
            label: 'Simplify',
            icon: <IconSimplify />,
            action: 'short',
        },
        // {
        //     label: 'Elaborate',
        //     icon: <IconElaborate />,
        //     action: 'elaborate',
        // },
    ];

    const handleActionSelect = (action: string) => {
        setSelectedAction(action);
        console.log(`Selected improvement type: ${action}`);
        dispatch(setRefinedText(''));
    };

    const handleImprovementAction = async (action: string) => {
        if (!currentProject?.id) {
            console.log('No current project ID available');
            return;
        }

        if (!selectedText || selectedText === 'No text selected') {
            console.log('No text selected for improvement');
            return;
        }

        console.log(`=== ${action.toUpperCase()} ACTION ===`);
        console.log('Highlighted/Selected text:', selectedText);
        console.log('Action type:', action);

        await dispatch(refineText({ input: selectedText, action: action }));
    };

    const handleRegenerate = async () => {
        if (!currentProject?.id) {
            console.log('No current project ID available for regeneration');
            return;
        }

        if (!selectedAction) {
            console.log('No improvement type selected');
            return;
        }

        if (!selectedText || selectedText === 'No text selected') {
            console.log('No text selected for improvement');
            return;
        }

        await handleImprovementAction(selectedAction);
    };

    const handleApply = async () => {
        if (!improvedText || !quillRef?.current) {
            console.warn('Missing improved text or quill reference');
            return;
        }

        const quill = quillRef.current.getEditor();
        const selection = quill.getSelection();

        if (!selection || selection.length === 0) {
            console.warn('No valid selection to replace');
            return;
        }

        const normalizedImprovedText = improvedText.replace(/\\n/g, '\n');

        quill.deleteText(selection.index, selection.length);
        quill.insertText(selection.index, normalizedImprovedText, 'user');

        quill.setSelection(selection.index, normalizedImprovedText.length);
    };

    return (
        <div className={s.block_container}>
            <div className={s.header_container}>
                <h3 className={s.bold_header} style={{ marginBottom: '10px' }}>
                    Make changes to the highlighted text
                </h3>
                <p className={s.text}>New text will appear here</p>
            </div>

            <div
                className={s.input}
                style={{
                    minHeight: '120px',
                    maxHeight: '240px',
                    overflowY: 'auto',
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    marginBottom: '20px',
                }}
            >
                {isGenerating ? (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>
                        Generating improved text...
                    </span>
                ) : improvedText ? (
                    <span style={{ color: '#2c5530', fontWeight: '500' }}>{improvedText}</span>
                ) : (
                    <span style={{ color: '#999' }}>
                        {selectedText
                            ? `Selected: "${selectedText}" `
                            : 'Highlight text in the editor and choose an improvement option'}
                    </span>
                )}
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '8px',
                    marginBottom: '30px',
                }}
            >
                {improvementOptions.map((option) => (
                    <Button
                        key={option.action}
                        onClick={() => handleActionSelect(option.action)}
                        state="enabled"
                        style={{
                            backgroundColor: selectedAction === option.action ? '#ffdd35' : '#fff',
                            border:
                                selectedAction === option.action
                                    ? '1px solid #000'
                                    : '1px solid #e9ecef',
                            borderRadius: '6px',
                            padding: '4px 6px',
                            fontSize: '10px',
                            color: '#303030',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            minHeight: '40px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow:
                                selectedAction === option.action
                                    ? '0 2px 4px rgba(0,0,0,0.1)'
                                    : 'none',
                            width: '100%',
                            fontWeight: selectedAction === option.action ? '600' : '400',
                        }}
                        icon={option.icon}
                        iconPosition="left"
                        text={option.label}
                    />
                ))}
            </div>

            <div
                style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                    padding: '0 16px',
                }}
            >
                <Button
                    onClick={handleRegenerate}
                    state={
                        isGenerating
                            ? 'loading'
                            : selectedAction && selectedText && selectedText !== 'No text selected'
                              ? 'enabled'
                              : 'disabled'
                    }
                    style={{
                        backgroundColor: '#ffdd35',
                        borderRadius: '6px',
                        padding: '6px 16px',
                        fontSize: '12px',
                        color: '#000',
                        border: 'none',
                        fontWeight: '600',
                        minWidth: '90px',
                    }}
                    text={isGenerating ? 'Generating...' : 'Regenerate'}
                />
                <Button
                    onClick={handleApply}
                    state={isGenerating || !improvedText ? 'disabled' : 'enabled'}
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e9ecef',
                        borderRadius: '6px',
                        padding: '3px 12px',
                        fontSize: '12px',
                        color: '#303030',
                        fontWeight: '600',
                        minWidth: '90px',
                    }}
                    text="Apply"
                />
            </div>
        </div>
    );
};

export default ImproveTextBlock;
